import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import OfficeReceiptPreviewModal from "./OfficeReceiptPreviewModal";
import { getOfficeOrderFinanceAnalytics } from "../../../actions/officeOrderFinanceAnalytics.actions";
import { setOfficeOrderModalTab } from "../../../actions/officeOrderModal.actions";
import { subscribeOfficeAnalyticsRefresh } from "../../../utils/officeAnalyticsEvents";

const rubFormatter = new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("ru-RU");

function toNumber(value, fallback = 0) {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
}

function formatMoney(value) {
    return rubFormatter.format(toNumber(value, 0));
}

function formatShortMoney(value) {
    const num = toNumber(value, 0);
    const abs = Math.abs(num);
    const sign = num < 0 ? "-" : "";

    if (abs >= 1000000) {
        return `${sign}${(abs / 1000000).toFixed(abs >= 10000000 ? 0 : 1).replace(".", ",")} млн ₽`;
    }

    if (abs >= 1000) {
        return `${sign}${Math.round(abs / 1000).toLocaleString("ru-RU")} тыс ₽`;
    }

    return `${sign}${abs.toLocaleString("ru-RU")} ₽`;
}

function getCurrentMonthKey() {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");

    return `${y}-${m}`;
}

function shiftMonth(monthKey, shift) {
    const [yearRaw, monthRaw] = String(monthKey || getCurrentMonthKey()).split("-");
    const year = Number(yearRaw);
    const monthIndex = Number(monthRaw) - 1;

    const date = new Date(year, monthIndex + shift, 1);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");

    return `${y}-${m}`;
}

function normalizeMonthKey(monthKey) {
    const fallback = getCurrentMonthKey();
    const [yearRaw, monthRaw] = String(monthKey || fallback).split("-");
    const year = Number(yearRaw);
    const month = Number(monthRaw);

    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
        return fallback;
    }

    return `${year}-${String(month).padStart(2, "0")}`;
}

function clampMonthKey(monthKey, minMonthKey, maxMonthKey) {
    const normalized = normalizeMonthKey(monthKey);
    const minKey = minMonthKey ? normalizeMonthKey(minMonthKey) : "";
    const maxKey = maxMonthKey ? normalizeMonthKey(maxMonthKey) : "";

    if (minKey && normalized < minKey) return minKey;
    if (maxKey && normalized > maxKey) return maxKey;

    return normalized;
}

function getMonthKeyFromDate(value, fallback = "") {
    if (!value) return fallback;

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return fallback;

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function formatActor(actor) {
    if (!actor) return "Неизвестный сотрудник";

    if (typeof actor === "string") return actor;

    const name = [actor.firstName, actor.lastName]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .join(" ");

    return (
        actor.displayName ||
        name ||
        actor.username ||
        actor.email ||
        actor._id ||
        "Неизвестный сотрудник"
    );
}

function formatDateTime(value) {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString("ru-RU");
}

function getOperationTypeLabel(type) {
    if (type === "office") return "Расход офиса";
    return "Оплата клиента";
}

function getOperationSign(type) {
    if (type === "office") return "-";
    return "+";
}

function getDayClassName(day, selectedDate) {
    return [
        "order-finance-calendar-AO__day",
        day?.isToday ? "is-today" : "",
        day?.date === selectedDate ? "is-selected" : "",
        day?.operationsCount > 0 ? "has-operations" : "",
        day?.clientPaidAmount > 0 ? "has-income" : "",
        day?.officeExpenseAmount > 0 ? "has-expense" : "",
        day?.netAmount > 0 ? "has-positive" : "",
        day?.netAmount < 0 ? "has-negative" : "",
    ].filter(Boolean).join(" ");
}

function SummaryCard({ title, value, variant = "", sub = "" }) {
    return (
        <div
            className={[
                "order-finance-summary-AO__card",
                variant ? `order-finance-summary-AO__card--${variant}` : "",
            ].filter(Boolean).join(" ")}
        >
            <span>{title}</span>
            <b>{formatMoney(value)}</b>
            {!!sub && <small>{sub}</small>}
        </div>
    );
}

function OperationItem({ item, onOpenReceipt }) {
    const files = Array.isArray(item?.itemPictures) ? item.itemPictures : [];
    const isExpense = item?.type === "office";
    const createdBy = formatActor(item?.createdBy);
    const updatedBy = formatActor(item?.updatedBy);
    const wasUpdated = Boolean(
        item?.updatedAt &&
        item?.createdAt &&
        new Date(item.updatedAt).getTime() - new Date(item.createdAt).getTime() > 1000
    );

    return (
        <div
            className={[
                "order-finance-operation-AO",
                isExpense ? "order-finance-operation-AO--expense" : "order-finance-operation-AO--income",
            ].join(" ")}
        >
            <div className="order-finance-operation-AO__top">
                <div>
                    <strong>{getOperationTypeLabel(item?.type)}</strong>
                    <span>{formatDateTime(item?.date)}</span>
                </div>

                <b>
                    {getOperationSign(item?.type)}
                    {formatMoney(item?.price)}
                </b>
            </div>

            <div className="order-finance-operation-AO__description">
                <div>{item?.description || "Без описания"}</div>
                <div className="order-finance-operation-AO__audit">
                    Внёс: {createdBy}
                    {item?.createdAt ? ` · записано ${formatDateTime(item.createdAt)}` : ""}
                </div>
                {wasUpdated ? (
                    <div className="order-finance-operation-AO__audit">
                        Изменил: {updatedBy} · {formatDateTime(item.updatedAt)}
                    </div>
                ) : null}
            </div>

            {!!files.length && (
                <div className="order-finance-operation-AO__files">
                    {files.map((file, index) => (
                        <button
                            key={file?._id || file?.receiptId || `${item?._id || "operation"}-${index}`}
                            type="button"
                            onClick={() => onOpenReceipt?.(item, files, index)}
                        >
                            Чек {index + 1}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function DayMoney({ type, label, value, prefix }) {
    if (!toNumber(value, 0)) return null;

    return (
        <span className={`order-finance-calendar-AO__money order-finance-calendar-AO__money--${type}`}>
            <small>{label}</small>
            <b>{prefix}{formatShortMoney(value)}</b>
        </span>
    );
}

export default function OrderFinanceCalendar({
    officeId,
    orderId,
}) {
    const dispatch = useDispatch();

    const {
        loading,
        error,
        order,
        finance,
        calendar,
        operations,
        range,
        monthKey: loadedMonthKey = "",
    } = useSelector((state) => state.officeOrderFinanceAnalytics || {});

    const [monthKey, setMonthKey] = useState(getCurrentMonthKey());
    const [selectedDate, setSelectedDate] = useState("");
    const [mode, setMode] = useState("calendar");
    const [receiptPreview, setReceiptPreview] = useState(null);

    const handleOpenReceipt = (paymentAccount, receipts, index = 0) => {
        const paymentAccountId = String(paymentAccount?._id || "");

        if (!paymentAccountId || !Array.isArray(receipts) || !receipts.length) return;

        setReceiptPreview({
            paymentAccountId,
            receipts,
            initialIndex: index,
            title: paymentAccount?.type === "office" ? "Чек расхода офиса" : "Чек оплаты клиента",
        });
    };


    const currentOrderLoaded =
        !!order?._id &&
        String(order._id) === String(orderId);

    const activeOrder = currentOrderLoaded ? order : null;
    const activeRange = currentOrderLoaded ? range : null;

    const minMonthKey =
        activeRange?.minMonthKey ||
        getMonthKeyFromDate(activeOrder?.createdAt, getCurrentMonthKey());
    const maxMonthKey =
        activeRange?.maxMonthKey ||
        getCurrentMonthKey();
    const boundedMonthKey = clampMonthKey(monthKey, minMonthKey, maxMonthKey);

    useEffect(() => {
        if (monthKey !== boundedMonthKey) {
            setMonthKey(boundedMonthKey);
        }
    }, [monthKey, boundedMonthKey]);

    const loadFinance = useCallback(() => {
        if (!officeId || !orderId) return;

        dispatch(getOfficeOrderFinanceAnalytics({
            officeId,
            orderId,
            month: boundedMonthKey,
        }));
    }, [dispatch, officeId, orderId, boundedMonthKey]);

    useEffect(() => {
        loadFinance();
    }, [loadFinance]);

    useEffect(() => {
        if (!officeId || !orderId) return undefined;

        return subscribeOfficeAnalyticsRefresh((payload) => {
            if (payload?.officeId && String(payload.officeId) !== String(officeId)) return;
            if (payload?.orderId && String(payload.orderId) !== String(orderId)) return;

            loadFinance();
        });
    }, [officeId, orderId, loadFinance]);

    const loadedMonthMatches =
        currentOrderLoaded &&
        !!calendar &&
        normalizeMonthKey(calendar?.monthKey || loadedMonthKey || boundedMonthKey) === boundedMonthKey;

    const visibleCalendar = loadedMonthMatches ? calendar : null;
    const visibleFinance = currentOrderLoaded ? finance : null;
    const allOperations =
        currentOrderLoaded && Array.isArray(operations)
            ? operations
            : [];

    useEffect(() => {
        if (!visibleCalendar?.days?.length) return;

        const today = visibleCalendar.days.find((day) => day.isToday);
        const firstWithOperations = visibleCalendar.days.find((day) => day.operationsCount > 0);
        const lastWithOperations = [...visibleCalendar.days].reverse().find((day) => day.operationsCount > 0);

        setSelectedDate((prev) => {
            if (prev && visibleCalendar.days.some((day) => day.date === prev)) return prev;

            return lastWithOperations?.date || firstWithOperations?.date || today?.date || visibleCalendar.days[0]?.date || "";
        });
    }, [visibleCalendar]);

    const days = Array.isArray(visibleCalendar?.days) ? visibleCalendar.days : [];

    const selectedDay = useMemo(() => {
        return days.find((day) => day.date === selectedDate) || null;
    }, [days, selectedDate]);

    const selectedOperations = selectedDay?.operations || [];

    const blanksBefore = useMemo(() => {
        const count = Math.max(0, toNumber(visibleCalendar?.firstWeekday, 1) - 1);

        return Array.from({ length: count });
    }, [visibleCalendar]);

    const activeDays = useMemo(() => {
        return days.filter((day) => day.operationsCount > 0);
    }, [days]);

    const selectedDayTitle = selectedDay
        ? new Date(selectedDay.date).toLocaleDateString("ru-RU", {
            day: "numeric",
            month: "long",
            year: "numeric",
        })
        : "Дата не выбрана";

    const canGoPrev = boundedMonthKey > minMonthKey;
    const canGoNext = boundedMonthKey < maxMonthKey;
    const isCurrentMonth = boundedMonthKey === getCurrentMonthKey();

    const changeMonth = (nextMonthKey) => {
        const bounded = clampMonthKey(nextMonthKey, minMonthKey, maxMonthKey);

        if (bounded === boundedMonthKey) return;

        setMonthKey(bounded);
        setSelectedDate("");
    };

    return (
        <div className="order-finance-AO">
            <div className="order-finance-AO__head">
                <div>
                    <div className="order-finance-AO__title">
                        Финансы заказа {activeOrder?.orderNumber || ""}
                    </div>

                    <div className="order-finance-AO__muted">
                        Платежи и расходы разложены по датам операций
                    </div>
                </div>

                <div className="order-finance-AO__head-actions">
                    <button
                        type="button"
                        className="btn"
                        onClick={() => dispatch(setOfficeOrderModalTab("payment"))}
                    >
                        Открыть оплату
                    </button>

                    <button
                        type="button"
                        className="btn"
                        onClick={loadFinance}
                        disabled={loading}
                    >
                        {loading ? "Обновляем..." : "Обновить"}
                    </button>
                </div>
            </div>

            {!!error && (
                <div className="order-modal-error-AO">
                    {error}
                </div>
            )}

            <div className="order-finance-summary-AO">
                <SummaryCard title="Сумма заказа" value={visibleFinance?.orderAmount ?? visibleFinance?.amount} />
                <SummaryCard title="Клиент оплатил" value={visibleFinance?.clientPaidAmount} variant="income" />
                <SummaryCard title="Расходы офиса" value={visibleFinance?.officeExpenseAmount} variant="expense" />
                <SummaryCard title="Чистыми" value={visibleFinance?.netAmount} variant="net" />
                <SummaryCard title="Осталось" value={visibleFinance?.remainingAmount} variant="warning" />
                <SummaryCard title="Переплата" value={visibleFinance?.overpaymentAmount} variant="warning" />
            </div>

            <div className="order-finance-control-AO">
                <div className="order-finance-mode-AO">
                    <button
                        type="button"
                        className={mode === "calendar" ? "is-active" : ""}
                        onClick={() => setMode("calendar")}
                    >
                        Календарь
                    </button>

                    <button
                        type="button"
                        className={mode === "operations" ? "is-active" : ""}
                        onClick={() => setMode("operations")}
                    >
                        Все операции
                        <span>{allOperations.length}</span>
                    </button>
                </div>

                <div className="order-finance-month-AO">
                    <button
                        type="button"
                        className="order-finance-month-AO__btn"
                        onClick={() => changeMonth(shiftMonth(boundedMonthKey, -1))}
                        disabled={!canGoPrev}
                        title={!canGoPrev ? "Раньше этого заказа операций быть не может" : "Предыдущий месяц"}
                    >
                        ←
                    </button>

                    <input
                        type="month"
                        value={boundedMonthKey}
                        min={minMonthKey}
                        max={maxMonthKey}
                        onChange={(event) => changeMonth(event.target.value || getCurrentMonthKey())}
                    />

                    <button
                        type="button"
                        className="order-finance-month-AO__btn"
                        onClick={() => changeMonth(shiftMonth(boundedMonthKey, 1))}
                        disabled={!canGoNext}
                        title={!canGoNext ? "Будущие месяцы недоступны" : "Следующий месяц"}
                    >
                        →
                    </button>

                    <button
                        type="button"
                        className="order-finance-month-AO__today"
                        onClick={() => changeMonth(getCurrentMonthKey())}
                        disabled={isCurrentMonth}
                    >
                        Текущий месяц
                    </button>
                </div>
            </div>

            {mode === "calendar" && loading && !visibleCalendar ? (
                <div className="ordersAO__loading">
                    Загружаем выбранный месяц…
                </div>
            ) : null}

            {mode === "calendar" && visibleCalendar && (
                <div className="order-finance-calendar-layout-AO">
                    <div className="order-finance-calendar-card-AO">
                        <div className="order-finance-calendar-card-AO__head">
                            <div>
                                <strong>{visibleCalendar?.monthLabel || boundedMonthKey}</strong>
                                <span>
                                    Дней с операциями: {activeDays.length} · операций: {visibleCalendar?.totals?.operationsCount || 0}
                                </span>
                            </div>

                            <div className="order-finance-calendar-card-AO__totals">
                                <span className="income">+{formatMoney(visibleCalendar?.totals?.clientPaidAmount)}</span>
                                <span className="expense">-{formatMoney(visibleCalendar?.totals?.officeExpenseAmount)}</span>
                                <span className="net">={formatMoney(visibleCalendar?.totals?.netAmount)}</span>
                            </div>
                        </div>

                        <div className="order-finance-calendar-AO">
                            <div className="order-finance-calendar-AO__weekdays">
                                <span>Пн</span>
                                <span>Вт</span>
                                <span>Ср</span>
                                <span>Чт</span>
                                <span>Пт</span>
                                <span>Сб</span>
                                <span>Вс</span>
                            </div>

                            <div className="order-finance-calendar-AO__grid">
                                {blanksBefore.map((_, index) => (
                                    <div
                                        key={`blank-${index}`}
                                        className="order-finance-calendar-AO__blank"
                                    />
                                ))}

                                {days.map((day) => (
                                    <button
                                        key={day.date}
                                        type="button"
                                        className={getDayClassName(day, selectedDate)}
                                        onClick={() => setSelectedDate(day.date)}
                                    >
                                        <span className="order-finance-calendar-AO__topline">
                                            <b>{day.day}</b>
                                            <small>{day.weekday}</small>
                                        </span>

                                        {day.operationsCount > 0 ? (
                                            <span className="order-finance-calendar-AO__content">
                                                <DayMoney
                                                    type="income"
                                                    label="Клиент"
                                                    value={day.clientPaidAmount}
                                                    prefix="+"
                                                />

                                                <DayMoney
                                                    type="expense"
                                                    label="Офис"
                                                    value={day.officeExpenseAmount}
                                                    prefix="-"
                                                />

                                                <DayMoney
                                                    type="net"
                                                    label="Итог"
                                                    value={day.netAmount}
                                                    prefix={day.netAmount > 0 ? "+" : ""}
                                                />
                                            </span>
                                        ) : (
                                            <span className="order-finance-calendar-AO__empty-day">
                                                нет операций
                                            </span>
                                        )}

                                        {!!day.operationsCount && (
                                            <span className="order-finance-calendar-AO__count">
                                                {day.operationsCount}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="order-finance-day-AO">
                        <div className="order-finance-day-AO__head">
                            <div>
                                <strong>{selectedDayTitle}</strong>
                                <span>
                                    Операций: {selectedDay?.operationsCount || 0}
                                </span>
                            </div>
                        </div>

                        <div className="order-finance-day-AO__summary">
                            <div className="income">
                                <span>Клиент</span>
                                <b>+{formatMoney(selectedDay?.clientPaidAmount)}</b>
                            </div>

                            <div className="expense">
                                <span>Офис</span>
                                <b>-{formatMoney(selectedDay?.officeExpenseAmount)}</b>
                            </div>

                            <div className="net">
                                <span>Итог</span>
                                <b>{formatMoney(selectedDay?.netAmount)}</b>
                            </div>
                        </div>

                        <div className="order-finance-day-AO__list">
                            {!selectedOperations.length ? (
                                <div className="order-finance-empty-AO">
                                    В этот день операций не было.
                                </div>
                            ) : (
                                selectedOperations.map((item) => (
                                    <OperationItem
                                        key={item._id}
                                        item={item}
                                        onOpenReceipt={handleOpenReceipt}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {mode === "operations" && (
                <div className="order-finance-operations-AO">
                    <div className="order-finance-operations-AO__head">
                        <div>
                            <strong>Все операции по заказу</strong>
                            <span>{numberFormatter.format(allOperations.length)} операций</span>
                        </div>
                    </div>

                    {!allOperations.length ? (
                        <div className="order-finance-empty-AO">
                            По заказу пока нет оплат или расходов.
                        </div>
                    ) : (
                        allOperations.map((item) => (
                            <OperationItem
                                key={item._id}
                                item={item}
                                onOpenReceipt={handleOpenReceipt}
                            />
                        ))
                    )}
                </div>
            )}

            <OfficeReceiptPreviewModal
                open={!!receiptPreview}
                paymentAccountId={receiptPreview?.paymentAccountId || ""}
                receipts={receiptPreview?.receipts || []}
                initialIndex={receiptPreview?.initialIndex || 0}
                title={receiptPreview?.title || "Чек"}
                onClose={() => setReceiptPreview(null)}
            />
        </div>
    );
}
